// src/components/ReturnsEntryV2.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  arrayUnion,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

/* =========================================================
   CONFIG
========================================================= */

const RETURNS_COL = "returns_v2";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "waiting_collection", label: "Waiting for collection" },
  { value: "waiting_processing", label: "Waiting to be processed" },
  { value: "in_progress", label: "In progress" },
  { value: "straggler", label: "Straggler" },
  { value: "rework_same_so", label: "Reworked same SO" },
  { value: "complete", label: "Complete" },
];

/* =========================================================
   INITIAL SHAPES
========================================================= */

const INITIAL_CYL = {
  returnsNoteNumber: "",
  serialNumber: "",
  sretNumber: "",
  cylinderModel: "",
  capacity: "",
  dateOfManufacture: "",
  reasonCode: "",
  reasonDefectCode: "",
  faultFoundCode: "",
  faultDefectCode: "",
  internalNotes: "",
  operators: { welded: "", airTest: "", hotTested: "", foamTested: "" },
};

const INITIAL_PARTS = {
  returnsNoteNumber: "",
  serialNumber: "",
  sretNumber: "",
  partCode: "",
  partTitle: "",
  partDescription: "",
  reportedCode: "",
  faultFoundCode: "",
  internalNotes: "",
};

const INITIAL_PARTS_TEST = {};

/* =========================================================
   SIMPLE IN-MEMORY CACHE
========================================================= */

let codeTablesCache = null;
const partTemplateCache = new Map();

/* =========================================================
   UTILS
========================================================= */

function normalize(str) {
  return String(str || "").trim().toLowerCase();
}

function digitsOnly(str) {
  return String(str || "").replace(/\D+/g, "");
}

function prefixes(str, min = 3, max = 16) {
  const s = normalize(str).replace(/\s+/g, "");
  const out = [];
  for (let i = min; i <= Math.min(max, s.length); i++) out.push(s.slice(0, i));
  return out;
}

function wordTokens(str) {
  const s = normalize(str);
  if (!s) return [];
  return s.split(/[^a-z0-9]+/g).filter(Boolean).slice(0, 40);
}

function buildReturnSearchIndex({ type, cyl, parts, partsTemplate, adminNotes }) {
  const f = type === "parts" ? (parts || {}) : (cyl || {});

  const serial = normalize(f.serialNumber);
  const rnRaw = normalize(f.returnsNoteNumber);
  const rnDigits = digitsOnly(f.returnsNoteNumber);
  const sret = normalize(f.sretNumber);

  const model = normalize(f.cylinderModel);
  const partCode = normalize(f.partCode || partsTemplate?.partCode);
  const partTitle = normalize(f.partTitle || partsTemplate?.title);
  const partDesc = normalize(f.partDescription || partsTemplate?.description);

  const tokens = new Set();

  prefixes(serial).forEach((p) => tokens.add(`sn:${p}`));
  prefixes(rnRaw).forEach((p) => tokens.add(`rn:${p}`));
  prefixes(rnDigits).forEach((p) => tokens.add(`rnn:${p}`));
  prefixes(sret).forEach((p) => tokens.add(`sret:${p}`));
  prefixes(partCode).forEach((p) => tokens.add(`pc:${p}`));

  [
    f.internalNotes,
    adminNotes,
    model,
    partCode,
    partTitle,
    partDesc,
    f.reportedCode,
    f.faultFoundCode,
    f.reasonCode,
    f.reasonDefectCode,
    f.faultDefectCode,
    f.capacity,
  ].forEach((v) => {
    wordTokens(v).forEach((w) => tokens.add(`w:${w}`));
  });

  const searchTokens = Array.from(tokens).slice(0, 500);

  return {
    serialLower: serial || "",
    rnDigits: rnDigits || "",
    sretLower: sret || "",
    partCodeLower: partCode || "",
    searchTokens,
  };
}

function safeUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `id_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

function buildEvent(action, note, user) {
  return {
    at: new Date().toISOString(),
    action,
    note,
    by: user,
  };
}

function buildDataPayload(type, cyl, parts, partsTemplate, partsTest) {
  return {
    cyl: type === "cyl" ? cyl : null,
    parts: type === "parts" ? parts : null,
    partsTemplate: type === "parts" ? partsTemplate : null,
    partsTest: type === "parts" ? partsTest : null,
  };
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
}

function FieldError({ text }) {
  return <p className="mt-1 text-sm text-red-600">{text}</p>;
}

function TextField({ label, value, onChange, placeholder = "", error, type = "text", readOnly = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        } ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
        value={value || ""}
        onChange={(e) => {
          if (!readOnly) onChange(e.target.value);
        }}
        placeholder={placeholder}
        readOnly={readOnly}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder = "", error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <textarea
        rows={4}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        }`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function ReturnsEntryV2() {
  const navigate = useNavigate();
  const location = useLocation();
  const qp = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const urlType = (qp.get("type") || "").toLowerCase() === "parts" ? "parts" : "cyl";
  const urlId = qp.get("id") || null;

  const [docType, setDocType] = useState(null);
  const type = docType || urlType;

  const [docId, setDocId] = useState(urlId);

  const [status, setStatus] = useState("new");
  const [createdAt, setCreatedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [storage, setStorage] = useState({ folderId: null, folderUrl: null });

  const [cyl, setCyl] = useState(INITIAL_CYL);
  const [parts, setParts] = useState(INITIAL_PARTS);
  const [partsTemplate, setPartsTemplate] = useState(null);
  const [partsTest, setPartsTest] = useState(INITIAL_PARTS_TEST);

  const [reasonOptions, setReasonOptions] = useState([]);
  const [defectOptions, setDefectOptions] = useState([]);

  const [loading, setLoading] = useState(!!urlId);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState("");

  const fns = useMemo(() => getFunctions(undefined, "europe-west2"), []);
  const user = useMemo(() => ({ uid: "unknown", name: "unknown" }), []);

  /* ---------------- Load code tables once with cache ---------------- */
  useEffect(() => {
    let cancelled = false;

    async function loadCodeTables() {
      if (codeTablesCache) {
        if (!cancelled) {
          setReasonOptions(codeTablesCache.reasonOptions);
          setDefectOptions(codeTablesCache.defectOptions);
        }
        return;
      }

      try {
        const [rs, ds] = await Promise.all([
          getDocs(collection(db, "return_reasons")),
          getDocs(collection(db, "defect_codes")),
        ]);

        const next = {
          reasonOptions: rs.docs.map((d) => d.data()).filter(Boolean),
          defectOptions: ds.docs.map((d) => d.data()).filter(Boolean),
        };

        codeTablesCache = next;

        if (!cancelled) {
          setReasonOptions(next.reasonOptions);
          setDefectOptions(next.defectOptions);
        }
      } catch {
        if (!cancelled) {
          setReasonOptions([]);
          setDefectOptions([]);
        }
      }
    }

    loadCodeTables();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------------- Load existing record ---------------- */
  useEffect(() => {
    let cancelled = false;

    async function loadRecord() {
      if (!urlId) {
        setDocId(null);
        setDocType(null);
        setStorage({ folderId: null, folderUrl: null });
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const snap = await getDoc(doc(db, RETURNS_COL, urlId));
        if (cancelled) return;

        if (!snap.exists()) {
          const q = new URLSearchParams(location.search);
          q.delete("id");
          navigate({ pathname: "/returns/new", search: `?${q.toString()}` }, { replace: true });
          setDocId(null);
          setDocType(null);
          setStorage({ folderId: null, folderUrl: null });
          setLoading(false);
          return;
        }

        const d = snap.data();
        const loadedType = d.type === "parts" ? "parts" : "cyl";

        setDocType(loadedType);
        setDocId(urlId);

        setStatus(d.tracking?.status || "new");
        setCreatedAt(d.tracking?.createdAt || null);
        setUpdatedAt(d.tracking?.updatedAt || null);
        setSubmittedAt(d.tracking?.submittedAt || null);
        setStorage({
          folderId: d.storage?.folderId || null,
          folderUrl: d.storage?.folderUrl || null,
        });

        if (loadedType === "cyl") {
          setCyl(d.data?.cyl || INITIAL_CYL);
          setParts(INITIAL_PARTS);
          setPartsTemplate(null);
          setPartsTest(INITIAL_PARTS_TEST);
        } else {
          setParts(d.data?.parts || INITIAL_PARTS);
          setPartsTemplate(d.data?.partsTemplate || null);
          setPartsTest(d.data?.partsTest || INITIAL_PARTS_TEST);
          setCyl(INITIAL_CYL);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setSaveError("Could not load return.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRecord();

    return () => {
      cancelled = true;
    };
  }, [urlId, navigate, location.search]);

  /* ---------------- Clear stale part template when part code changes ---------------- */
  useEffect(() => {
    if (type !== "parts") return;
    const currentCode = (parts.partCode || "").trim().toLowerCase();
    const loadedCode = (partsTemplate?.partCode || "").trim().toLowerCase();

    if (partsTemplate && currentCode && loadedCode && currentCode !== loadedCode) {
      setPartsTemplate(null);
      setPartsTest({});
      setParts((prev) => ({
        ...prev,
        partTitle: "",
        partDescription: "",
      }));
    }
  }, [parts.partCode, partsTemplate, type]);

  /* ---------------- Load part template with cache ---------------- */
  const loadPartTemplate = useCallback(async (partCode) => {
    const code = (partCode || "").trim();
    if (!code) {
      setPartsTemplate(null);
      setPartsTest({});
      return;
    }

    const cacheKey = code.toLowerCase();

    try {
      let snapshot = partTemplateCache.get(cacheKey);

      if (!snapshot) {
        const snap = await getDoc(doc(db, "part_templates", code));

        if (!snap.exists()) {
          setPartsTemplate(null);
          setParts((p) => ({
            ...p,
            partTitle: "",
            partDescription: "",
          }));
          setPartsTest({});
          return;
        }

        const tpl = snap.data();
        snapshot = {
          partCode: tpl.partCode || code,
          title: tpl.title || "",
          description: tpl.description || "",
          codes: tpl.codes || [],
          steps: tpl.steps || [],
        };

        partTemplateCache.set(cacheKey, snapshot);
      }

      setPartsTemplate(snapshot);
      setParts((p) => ({
        ...p,
        partCode: snapshot.partCode,
        partTitle: snapshot.title,
        partDescription: snapshot.description,
      }));

      const nextTest = {};
      (snapshot.steps || []).forEach((step) => {
        nextTest[step.id] = step.type === "check" ? false : "";
      });
      setPartsTest(nextTest);
    } catch (err) {
      console.error(err);
      setSaveError("Could not load part template.");
    }
  }, []);

  /* ---------------- Validation ---------------- */
  const validate = useCallback(() => {
    const e = {};

    if (type === "cyl") {
      if (!cyl.serialNumber?.trim()) e.serialNumber = "Serial number is required.";
      if (!cyl.cylinderModel?.trim()) e.cylinderModel = "Cylinder model is required.";
      if (!cyl.reasonCode) e.reasonCode = "Pick a return reason.";
      if (!cyl.faultFoundCode) e.faultFoundCode = "Pick a fault found code.";
    } else {
      if (!parts.serialNumber?.trim()) e.serialNumber = "Serial number is required.";
      if (!parts.partCode?.trim()) e.partCode = "Part code is required.";
      if (!partsTemplate?.partCode) e.template = "Load a part template for that part code.";
      if (!parts.reportedCode) e.reportedCode = "Pick what was reported.";
      if (!parts.faultFoundCode) e.faultFoundCode = "Pick what you found.";

      (partsTemplate?.steps || []).forEach((step) => {
        if (!step.required) return;
        const val = partsTest[step.id];
        const missing = step.type === "check" ? !val : String(val || "").trim() === "";
        if (missing) e[`step:${step.id}`] = `Required: ${step.label}`;
      });
    }

    return e;
  }, [type, cyl, parts, partsTemplate, partsTest]);

  /* ---------------- Create initial record ---------------- */
  const ensureExists = useCallback(async () => {
    if (docId) return docId;

    const newId = safeUUID();
    const ref = doc(db, RETURNS_COL, newId);

    const searchIndex = buildReturnSearchIndex({
      type,
      cyl,
      parts,
      partsTemplate,
      adminNotes: "",
    });

    await setDoc(ref, {
      type,
      version: 1,
      source: "pthelpdesk",
      ...searchIndex,
      tracking: {
        status,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        submittedAt: null,
        statusUpdatedAt: serverTimestamp(),
      },
      data: buildDataPayload(type, cyl, parts, partsTemplate, partsTest),
      storage: {
        provider: "sharepoint",
        folderId: null,
        folderUrl: null,
      },
      events: [buildEvent("created", "Record created", user)],
    });

    setDocId(newId);
    setDocType(type);

    const q = new URLSearchParams(location.search);
    q.set("type", type);
    q.set("id", newId);
    navigate({ pathname: "/returns/new", search: `?${q.toString()}` }, { replace: true });

    try {
      const create = httpsCallable(fns, "spEnsureFolder");
      const { data } = await create({ type: "returnsImage", subId: newId });

      await updateDoc(ref, {
        "storage.provider": "sharepoint",
        "storage.folderId": data.folderItemId,
        "storage.folderUrl": data.folderWebUrl,
        "tracking.updatedAt": serverTimestamp(),
        events: arrayUnion(buildEvent("folder_created", "SharePoint folder created", user)),
      });

      setStorage({
        folderId: data.folderItemId,
        folderUrl: data.folderWebUrl,
      });
    } catch {
      // non-fatal
    }

    return newId;
  }, [docId, type, cyl, parts, partsTemplate, partsTest, status, user, location.search, navigate, fns]);

  /* ---------------- Save current state ---------------- */
  const onSave = useCallback(async () => {
    setSaveError("");
    setErrors({});
    setBusy(true);

    try {
      const id = await ensureExists();
      const ref = doc(db, RETURNS_COL, id);

      const searchIndex = buildReturnSearchIndex({
        type,
        cyl,
        parts,
        partsTemplate,
        adminNotes: "",
      });

      await updateDoc(ref, {
        serialLower: searchIndex.serialLower,
        rnDigits: searchIndex.rnDigits,
        sretLower: searchIndex.sretLower,
        partCodeLower: searchIndex.partCodeLower,
        searchTokens: searchIndex.searchTokens,

        "tracking.status": status,
        "tracking.updatedAt": serverTimestamp(),
        "tracking.statusUpdatedAt": serverTimestamp(),
        "tracking.submittedAt": submittedAt || null,

        "data.cyl": type === "cyl" ? cyl : null,
        "data.parts": type === "parts" ? parts : null,
        "data.partsTemplate": type === "parts" ? partsTemplate : null,
        "data.partsTest": type === "parts" ? partsTest : null,

        events: arrayUnion(buildEvent("saved", "Manual save", user)),
      });

      setUpdatedAt(new Date().toISOString());
    } catch (err) {
      console.error(err);
      setSaveError("Could not save.");
    } finally {
      setBusy(false);
    }
  }, [ensureExists, type, cyl, parts, partsTemplate, partsTest, status, submittedAt, user]);

  /* ---------------- Submit ---------------- */
  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSaveError("");

    const eMap = validate();
    setErrors(eMap);
    if (Object.keys(eMap).length) return;

    setBusy(true);

    try {
      const id = await ensureExists();
      const ref = doc(db, RETURNS_COL, id);

      const searchIndex = buildReturnSearchIndex({
        type,
        cyl,
        parts,
        partsTemplate,
        adminNotes: "",
      });

      await updateDoc(ref, {
        serialLower: searchIndex.serialLower,
        rnDigits: searchIndex.rnDigits,
        sretLower: searchIndex.sretLower,
        partCodeLower: searchIndex.partCodeLower,
        searchTokens: searchIndex.searchTokens,

        "tracking.status": status,
        "tracking.updatedAt": serverTimestamp(),
        "tracking.statusUpdatedAt": serverTimestamp(),
        "tracking.submittedAt": serverTimestamp(),

        "data.cyl": type === "cyl" ? cyl : null,
        "data.parts": type === "parts" ? parts : null,
        "data.partsTemplate": type === "parts" ? partsTemplate : null,
        "data.partsTest": type === "parts" ? partsTest : null,

        events: arrayUnion(buildEvent("submitted", "Return submitted", user)),
      });

      setSubmittedAt(new Date().toISOString());
      setUpdatedAt(new Date().toISOString());
    } catch (err) {
      console.error(err);
      setSaveError("Submit failed.");
    } finally {
      setBusy(false);
    }
  }, [ensureExists, validate, type, cyl, parts, partsTemplate, partsTest, status, user]);

  /* ---------------- Type chooser ---------------- */
  const chooseType = useCallback((next) => {
    if (docId) return;

    const q = new URLSearchParams(location.search);
    q.set("type", next);
    q.delete("id");
    navigate({ pathname: "/returns/new", search: `?${q.toString()}` }, { replace: true });
  }, [docId, location.search, navigate]);

  /* ---------------- Status change ---------------- */
  const changeStatus = useCallback(async (next) => {
    setStatus(next);

    if (!docId) return;

    try {
      await updateDoc(doc(db, RETURNS_COL, docId), {
        "tracking.status": next,
        "tracking.updatedAt": serverTimestamp(),
        "tracking.statusUpdatedAt": serverTimestamp(),
        "tracking.submittedAt": submittedAt || null,
        events: arrayUnion(buildEvent("status_changed", `Status -> ${next}`, user)),
      });
    } catch (err) {
      console.error(err);
      setSaveError("Status update failed.");
    }
  }, [docId, submittedAt, user]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 py-6">Loading…</div>;
  }

  const title = type === "parts" ? "Returns — Parts (V2)" : "Returns — Cylinder (V2)";

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <h1 className="text-3xl font-bold text-gray-900">{title}</h1>

        {!docId ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => chooseType("cyl")}
              className={`px-4 py-2 rounded-md border ${
                type === "cyl" ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-300 hover:bg-gray-50"
              }`}
            >
              Cylinder
            </button>
            <button
              type="button"
              onClick={() => chooseType("parts")}
              className={`px-4 py-2 rounded-md border ${
                type === "parts" ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-300 hover:bg-gray-50"
              }`}
            >
              Parts
            </button>
          </div>
        ) : (
          <span className="px-3 py-2 rounded-md bg-gray-100 border text-sm">
            Type locked: <b>{type === "parts" ? "Parts" : "Cylinder"}</b>
          </span>
        )}

        <div className="ml-0 sm:ml-4">
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => changeStatus(e.target.value)}
            className="text-sm rounded-md border border-gray-300 px-3 py-2"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <span className="ml-auto text-sm text-gray-500">
          ID: {docId ? docId.slice(-8) : "none"} · {submittedAt ? "Submitted" : "Draft"}
        </span>

        {saveError && <span className="text-sm text-red-600">{saveError}</span>}

        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          {busy ? "Working…" : "Save"}
        </button>

        <button
          type="button"
          onClick={() => navigate("/returns")}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-10">
        {type === "cyl" ? (
          <>
            <Section id="return-cylinder" title="Return & Cylinder">
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Returns Note Number" value={cyl.returnsNoteNumber} onChange={() => {}} readOnly />
                <TextField
                  label="Serial Number"
                  value={cyl.serialNumber}
                  onChange={(v) => setCyl((p) => ({ ...p, serialNumber: v }))}
                  error={errors.serialNumber}
                />
                <TextField
                  label="SRET Number"
                  value={cyl.sretNumber}
                  onChange={(v) => setCyl((p) => ({ ...p, sretNumber: v }))}
                />
                <TextField
                  label="Cylinder Model"
                  value={cyl.cylinderModel}
                  onChange={(v) => setCyl((p) => ({ ...p, cylinderModel: v }))}
                  error={errors.cylinderModel}
                />
                <TextField
                  label="Capacity"
                  value={cyl.capacity}
                  onChange={(v) => setCyl((p) => ({ ...p, capacity: v }))}
                  placeholder="e.g., 200L"
                />
                <TextField
                  label="Date of Manufacture"
                  type="date"
                  value={cyl.dateOfManufacture}
                  onChange={(v) => setCyl((p) => ({ ...p, dateOfManufacture: v }))}
                />
              </div>
            </Section>

            <Section id="reasoning" title="Reasons & Fault Found (coded)">
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField
                  label="Return Reason Code"
                  value={cyl.reasonCode}
                  onChange={(v) => setCyl((p) => ({ ...p, reasonCode: v }))}
                  error={errors.reasonCode}
                  placeholder="e.g., RC01"
                />
                <TextField
                  label="Return Defect Code (optional)"
                  value={cyl.reasonDefectCode}
                  onChange={(v) => setCyl((p) => ({ ...p, reasonDefectCode: v }))}
                  placeholder="e.g., D01"
                />
                <TextField
                  label="Fault Found Code"
                  value={cyl.faultFoundCode}
                  onChange={(v) => setCyl((p) => ({ ...p, faultFoundCode: v }))}
                  error={errors.faultFoundCode}
                  placeholder="e.g., FF01"
                />
                <TextField
                  label="Fault Defect Code (optional)"
                  value={cyl.faultDefectCode}
                  onChange={(v) => setCyl((p) => ({ ...p, faultDefectCode: v }))}
                  placeholder="e.g., D02"
                />

                {(reasonOptions.length > 0 || defectOptions.length > 0) && (
                  <div className="sm:col-span-2 text-xs text-gray-500">
                    Code tables loaded. Wire these to dropdowns when you are ready.
                  </div>
                )}
              </div>
            </Section>

            <Section id="notes" title="Notes">
              <TextArea
                label="Internal Notes"
                value={cyl.internalNotes}
                onChange={(v) => setCyl((p) => ({ ...p, internalNotes: v }))}
              />
            </Section>
          </>
        ) : (
          <>
            <Section id="return-part" title="Return & Part">
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Returns Note Number" value={parts.returnsNoteNumber} onChange={() => {}} readOnly />
                <TextField
                  label="Serial Number"
                  value={parts.serialNumber}
                  onChange={(v) => setParts((p) => ({ ...p, serialNumber: v }))}
                  error={errors.serialNumber}
                />
                <TextField
                  label="SRET Number"
                  value={parts.sretNumber}
                  onChange={(v) => setParts((p) => ({ ...p, sretNumber: v }))}
                />

                <div className="sm:col-span-2 grid grid-cols-[1fr_auto] gap-2">
                  <TextField
                    label="Part Code"
                    value={parts.partCode}
                    onChange={(v) => setParts((p) => ({ ...p, partCode: v }))}
                    error={errors.partCode || errors.template}
                  />
                  <div className="self-end">
                    <button
                      type="button"
                      className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                      onClick={() => loadPartTemplate(parts.partCode)}
                      disabled={busy}
                    >
                      Load
                    </button>
                  </div>
                </div>

                <TextField label="Title" value={parts.partTitle} onChange={() => {}} readOnly />
                <TextField label="Description" value={parts.partDescription} onChange={() => {}} readOnly />
              </div>
            </Section>

            <Section id="codes" title="Reported Reason & Fault Found">
              {!partsTemplate ? (
                <p className="text-sm text-gray-500">Load a part template to pick codes.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  <TextField
                    label="Reported Code"
                    value={parts.reportedCode}
                    onChange={(v) => setParts((p) => ({ ...p, reportedCode: v }))}
                    error={errors.reportedCode}
                    placeholder="e.g., R01"
                  />
                  <TextField
                    label="Fault Found Code"
                    value={parts.faultFoundCode}
                    onChange={(v) => setParts((p) => ({ ...p, faultFoundCode: v }))}
                    error={errors.faultFoundCode}
                    placeholder="e.g., F01"
                  />
                </div>
              )}
            </Section>

            <Section id="procedure" title="Test Procedure">
              {!partsTemplate ? (
                <p className="text-sm text-gray-500">Load a part template to see the test procedure.</p>
              ) : (partsTemplate.steps || []).length === 0 ? (
                <p className="text-sm text-gray-500">No procedure steps defined for this template.</p>
              ) : (
                <div className="grid gap-3">
                  {(partsTemplate.steps || []).map((step) => {
                    const errKey = `step:${step.id}`;
                    const err = errors[errKey];

                    if (step.type === "check") {
                      return (
                        <label
                          key={step.id}
                          className="flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={!!partsTest[step.id]}
                            onChange={() => setPartsTest((p) => ({ ...p, [step.id]: !p[step.id] }))}
                          />
                          <span className="text-sm text-gray-800">
                            {step.label}
                            {step.required ? " *" : ""}
                          </span>
                          {err && <div className="ml-auto"><FieldError text={err} /></div>}
                        </label>
                      );
                    }

                    return (
                      <div key={step.id}>
                        <TextField
                          label={`${step.label}${step.required ? " *" : ""}`}
                          value={partsTest[step.id] || ""}
                          onChange={(v) => setPartsTest((p) => ({ ...p, [step.id]: v }))}
                          error={err}
                          placeholder={step.placeholder || ""}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>

            <Section id="notes" title="Notes">
              <TextArea
                label="Internal Notes"
                value={parts.internalNotes}
                onChange={(v) => setParts((p) => ({ ...p, internalNotes: v }))}
              />
            </Section>
          </>
        )}

        <ReturnsPhotosV2
          returnsId={docId}
          returnsCol={RETURNS_COL}
          storage={storage}
          setStorage={setStorage}
        />

        <div className="fixed bottom-4 left-0 right-0 px-4">
          <div className="max-w-7xl mx-auto bg-white border border-gray-200 shadow-xl rounded-xl p-3 flex items-center gap-3">
            <span className="text-sm text-gray-600">
              ID: {docId ? docId.slice(-8) : "none"} · Status: {STATUS_OPTIONS.find((s) => s.value === status)?.label || status} · {submittedAt ? "Submitted" : "Draft"}
            </span>

            <button
              type="button"
              onClick={onSave}
              className="ml-auto px-4 py-3 rounded-md border border-gray-300 hover:bg-gray-50"
              disabled={busy}
            >
              {busy ? "Working…" : "Save"}
            </button>

            <button
              type="submit"
              disabled={busy}
              className={`px-6 py-3 rounded-md text-white transition ${
                busy ? "bg-indigo-300 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              {busy ? "Working…" : "Submit Return"}
            </button>
          </div>
        </div>

        <div className="h-24" />
      </form>
    </div>
  );
}

/* =========================================================
   PHOTOS
========================================================= */

function ReturnsPhotosV2({ returnsId, returnsCol, storage, setStorage }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const fns = useMemo(() => getFunctions(undefined, "europe-west2"), []);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const folderId = storage?.folderId || null;
  const folderUrl = storage?.folderUrl || null;

  const refreshImages = useCallback(async () => {
    if (!folderId) {
      setImages([]);
      return;
    }

    setLoading(true);
    try {
      const list = httpsCallable(fns, "spListImages");
      const { data } = await list({ folderItemId: folderId });
      setImages(Array.isArray(data?.images) ? data.images : []);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to load images");
    } finally {
      setLoading(false);
    }
  }, [fns, folderId]);

  useEffect(() => {
    refreshImages();
  }, [refreshImages]);

  async function getUploadUrl(fileName) {
    const create = httpsCallable(fns, "spCreateUploadSession");
    const { data } = await create({ folderItemId: folderId, fileName });
    return data.uploadUrl;
  }

  async function put(uploadUrl, file) {
    const res = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Length": String(file.size),
        "Content-Range": `bytes 0-${file.size - 1}/${file.size}`,
      },
      body: file,
    });

    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  }

  const onPick = async (files) => {
    if (!folderId) return;

    const arr = Array.from(files || []);
    if (!arr.length) return;

    setBusy(true);
    try {
      for (const file of arr) {
        const uploadUrl = await getUploadUrl(file.name);
        await put(uploadUrl, file);
      }
      await refreshImages();
    } catch (err) {
      console.error(err);
      alert(err.message || "Upload failed");
    } finally {
      setBusy(false);
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const createFolderNow = async () => {
    if (!returnsId) return;

    try {
      setBusy(true);

      const create = httpsCallable(fns, "spEnsureFolder");
      const { data } = await create({ type: "returnsImage", subId: returnsId });

      await updateDoc(doc(db, returnsCol, returnsId), {
        "storage.provider": "sharepoint",
        "storage.folderId": data.folderItemId,
        "storage.folderUrl": data.folderWebUrl,
        "tracking.updatedAt": serverTimestamp(),
        events: arrayUnion({
          at: new Date().toISOString(),
          action: "folder_created",
          note: "SharePoint folder created manually",
          by: { uid: "unknown", name: "unknown" },
        }),
      });

      setStorage({
        folderId: data.folderItemId,
        folderUrl: data.folderWebUrl,
      });
    } catch (err) {
      console.error(err);
      alert(err.message || "Could not create folder");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section id="photos" title="Photos (optional)">
      {!returnsId ? (
        <p className="text-sm text-gray-500">Save once to create the record, then you can attach photos.</p>
      ) : !folderId ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-600">Folder not ready yet.</p>
          <button
            type="button"
            onClick={createFolderNow}
            disabled={busy}
            className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          >
            {busy ? "Creating…" : "Create folder"}
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              onClick={() => cameraInputRef.current?.click()}
              disabled={busy}
            >
              📷 Take photo
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />

            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              onClick={() => galleryInputRef.current?.click()}
              disabled={busy}
            >
              🖼️ Upload from gallery
            </button>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />

            {folderUrl && (
              <a className="ml-auto text-sm underline text-indigo-700" href={folderUrl} target="_blank" rel="noreferrer">
                Open folder
              </a>
            )}

            <button
              type="button"
              onClick={refreshImages}
              disabled={loading || busy}
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500">Loading images…</p>
          ) : images.length === 0 ? (
            <p className="text-sm text-gray-500">No images uploaded.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((img) => (
                <a key={img.id} href={img.webUrl} target="_blank" rel="noreferrer" className="border rounded overflow-hidden">
                  <img
                    src={img.thumbUrl || img.webUrl}
                    alt={img.name}
                    className="w-full h-36 object-cover"
                    loading="lazy"
                  />
                  <div className="p-2 text-xs truncate">{img.name}</div>
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
}